import React from "react";

/**
 * Wise-style transfer/conversion breakdown.
 *
 * Displays:
 *   You send exactly        , sourceAmount in source currency
 *   Total fees (included)   , fee deducted from source
 *   Total amount we'll convert, sourceAmount − fee
 *   Guaranteed rate         , 1 src = X dst (mid-market, no markup)
 *   Recipient gets / You receive, destAmount
 *
 * Expected quote shape (any of):
 *   sourceAmount, sourceCurrency, destAmount, destCurrency,
 *   midRate | rate | fxRate | exchangeRate,
 *   transferFee | exchangeFee | fee | fees,
 *   amountToConvert, totalDeducted, hasSufficientBalance.
 */
const fmt = (n, ccy) =>
  n == null
    ? "-"
    : `${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}${ccy ? " " + ccy : ""}`;

const FeeBreakdown = ({ quote, kind = "auto", className = "" }) => {
  if (!quote) return null;

  const sourceCcy = quote.sourceCurrency;
  const destCcy = quote.destCurrency;

  const rateNum = Number(
    quote.midRate ?? quote.rate ?? quote.fxRate ?? quote.exchangeRate
  );
  const showRate = Number.isFinite(rateNum) && rateNum > 0;

  const isConversion =
    kind === "conversion" ||
    (kind === "auto" && quote.exchangeFee != null);
  const feeAmount = isConversion
    ? quote.exchangeFee ?? quote.fee
    : quote.transferFee ?? quote.fee ?? quote.fees;

  const sourceAmount = Number(quote.sourceAmount || 0);
  const feeNum = Number(feeAmount || 0);
  const amountToConvert =
    quote.amountToConvert != null
      ? Number(quote.amountToConvert)
      : Math.max(0, sourceAmount - feeNum);

  return (
    <div
      className={`d-flex flex-column gap-2 p-3 rounded-3 ${className}`}
      style={{
        backgroundColor: "rgba(27, 187, 107, 0.05)",
        border: "1px solid rgba(27, 187, 107, 0.25)",
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <span className="fs12px fw-semibold" style={{ color: "#1bbb6b" }}>
          Transfer details
        </span>
        {!quote.hasSufficientBalance && (
          <span
            className="fs12px fw-medium px-2 py-1 rounded-pill"
            style={{ backgroundColor: "rgba(252, 85, 91, 0.15)", color: "#FC555B" }}
          >
            Insufficient balance
          </span>
        )}
      </div>

      <div className="d-flex justify-content-between fs12px">
        <span style={{ color: "#7D7D7D" }}>You send exactly</span>
        <span className="fw-bold">{fmt(sourceAmount, sourceCcy)}</span>
      </div>

      {feeAmount != null && (
        <div className="d-flex justify-content-between fs12px">
          <span style={{ color: "#7D7D7D" }}>Total fees (included)</span>
          <span className="fw-medium">{fmt(feeNum, sourceCcy)}</span>
        </div>
      )}

      <div className="d-flex justify-content-between fs12px">
        <span style={{ color: "#7D7D7D" }}>Total amount we&apos;ll convert</span>
        <span className="fw-medium">{fmt(amountToConvert, sourceCcy)}</span>
      </div>

      {showRate && (
        <div className="d-flex justify-content-between fs12px">
          <span style={{ color: "#7D7D7D" }}>Guaranteed rate</span>
          <span className="fw-medium">
            1 {sourceCcy} = {rateNum.toFixed(6)} {destCcy}
          </span>
        </div>
      )}

      <hr className="m-0 opacity-25" />

      {quote.destAmount != null && (
        <div className="d-flex justify-content-between fs12px">
          <span className="fw-semibold">
            {isConversion ? "You receive" : "Recipient gets"}
          </span>
          <span className="fw-bold" style={{ color: "#1bbb6b" }}>
            {fmt(quote.destAmount, destCcy)}
          </span>
        </div>
      )}
    </div>
  );
};

export default FeeBreakdown;
